import { useMemo, useRef, type JSX } from "react";
import { useDialogKeyboard } from "../../components/ui/useDialogKeyboard";
import { createPortal } from "react-dom";
import { useCharacterUnlock } from "../../api/hooks/useCharacterUnlock";
import { CharacterCodexEntry } from "./CharacterCodexEntry";

export type CharacterCodexViewProps = {
  /** modal が開いているか。false のときは何も描画しない (Req 5.5)。 */
  open: boolean;
  /** 閉じる統合コールバック (✕ ボタン / 背景 click / Esc 共通)。 */
  onClose: () => void;
  /** 解放対象の worldline (= playline) ID。 */
  playlineId: string;
  // character-arc-history Phase 3: optional context、 渡された level 3 entry で「成長の軌跡」
  // timeline collapsible section が active になる。 不在経路では既存挙動 bit-by-bit 互換。
  scenarioId?: string;
  apiBaseUrl?: string;
};

/**
 * character-unlock spec のキャラ図鑑モーダル (Task 3.2)。
 *
 * - open=false で `return null` (Req 3.5, 5.5: DOM ゼロ)
 * - useCharacterUnlock で当該 playline の解放結果を fetch
 * - level 降順 (3 → 2 → 1) で並べ、同 level 内は characterId 昇順 (server 側 sort 済の前提だが defense-in-depth で再 sort)
 * - hiddenCount > 0 のときは末尾に「未発見: N キャラ」 line を追加 (Req 3.3、level 0 の個別 entry は出さない)
 * - modal は document.body 直下 portal、z-50 + bg-reader-page で本流 reader styling と独立 (Req 3.6)
 * - close trigger: ✕ ボタン / 背景 click / Esc キー
 *
 * Toast (UnlockToast) は本 task の範囲外。PlayerScreen 配線 (Task 4.1) で別途 mount される。
 */
export function CharacterCodexView({
  open,
  onClose,
  playlineId,
  scenarioId,
  apiBaseUrl,
}: CharacterCodexViewProps): JSX.Element | null {
  const dialogRef = useRef<HTMLDivElement>(null);
  // open=false のときは hook を呼んでも enabled=false で fetch されないが、
  // 早期 return の前に呼ばないと React Hooks ルール違反になるため open=true 経路で呼ぶ。
  // open=false で return null するため、modal 閉時の fetch ゼロは保たれる。
  const query = useCharacterUnlock(open ? playlineId : null);

  // Escape close + initial focus + Tab focus trap を hook に委譲 (= SettingsPanel pattern)。
  useDialogKeyboard({ containerRef: dialogRef, open, onClose });

  // server 側でも level 降順 + characterId 昇順で sort 済の前提だが、
  // 表示順の決定論性を保つため client 側でも再 sort (defense-in-depth)。
  const sortedCharacters = useMemo(() => {
    const data = query.data;
    if (data === undefined) return [];
    return [...data.unlockedCharacters].sort(
      (a, b) =>
        b.level - a.level || a.characterId.localeCompare(b.characterId),
    );
  }, [query.data]);

  if (!open) return null;

  const hiddenCount = query.data?.hiddenCount ?? 0;

  return createPortal(
    <div
      data-testid="character-codex-view"
      className="fixed inset-0 z-50 flex items-start justify-center bg-reader-overlay overflow-y-auto p-4 sm:p-6"
      onClick={(e) => {
        // backdrop (自分自身) click のみ close。子要素 click は currentTarget 判定で除外。
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="キャラ図鑑"
        className="relative w-full max-w-2xl bg-reader-page text-reader-primary rounded-lg shadow-xl mt-12 p-6 max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">キャラ図鑑</h2>
          <button
            type="button"
            data-testid="character-codex-close"
            onClick={onClose}
            className="inline-flex h-11 w-11 items-center justify-center text-reader-secondary hover:text-reader-primary"
            aria-label="閉じる"
          >
            ✕
          </button>
        </div>

        <div className="overflow-auto flex-1 space-y-2">
          {query.isLoading && (
            <div
              data-testid="character-codex-loading"
              className="text-sm text-reader-secondary"
            >
              読み込み中…
            </div>
          )}

          {query.isError && (
            <div
              data-testid="character-codex-error"
              role="alert"
              className="text-sm text-rose-300"
            >
              キャラ図鑑の取得に失敗しました
            </div>
          )}

          {query.isSuccess && (
            <>
              {sortedCharacters.length === 0 && hiddenCount === 0 && (
                <div
                  data-testid="character-codex-empty"
                  className="text-sm text-reader-secondary"
                >
                  まだ発見されたキャラがいません
                </div>
              )}

              {sortedCharacters.map((character) => {
                const entryProps: {
                  character: typeof character;
                  scenarioId?: string;
                  apiBaseUrl?: string;
                } = { character };
                if (scenarioId !== undefined) entryProps.scenarioId = scenarioId;
                if (apiBaseUrl !== undefined) entryProps.apiBaseUrl = apiBaseUrl;
                return (
                  <CharacterCodexEntry
                    key={character.characterId}
                    {...entryProps}
                  />
                );
              })}

              {hiddenCount > 0 && (
                <div
                  data-testid="character-codex-hidden-count"
                  className="text-sm text-reader-muted pt-2 border-t border-reader-border"
                >
                  未発見: {hiddenCount} キャラ
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
